import { API_METHODS } from '@compass/client/constants/api-methods';
import type { Asset } from '@compass/client/types/asset';
import type { CallInfo } from '@compass/client/types/call';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { FieldGroupAccordion } from '@components/ui/field-group-accordion';
import { SimpleTooltip } from '@components/ui/tooltip';
import { useSchema } from '@hooks/useSchema';
import { useRenderTemplate, useTemplates } from '@hooks/useTemplates';
import { useStore } from '@store/index';
import { createAllAccordionIndices, toggleAllAccordionItems } from '@utils/accordion';
import {
  type ErrorWithDetails,
  formatErrorMessage,
  humanizeFieldErrorInline,
} from '@utils/errorMessageFormatter';
import { motion } from 'framer-motion';
import {
  ChevronsDownUp,
  CircleAlert,
  Download,
  Edit3,
  Globe,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CaseStateReasonSection } from '@/components/CaseStateReasonSection';
import { ManufacturerAckSection } from '@/components/ManufacturerAckSection';
import { MultipleRecordingsBanner } from '@/components/MultipleRecordingsBanner';
import { PermissionGuard } from '@/components/PermissionGuard';
import { RecordingSelectionModal } from '@/components/RecordingSelectionModal';
import { KnipperCaseState } from '@/constants/caseStates';
import { useCall, useCaseRecordCalls } from '@/hooks/useCalls';
import { useCaseInputStorage } from '@/hooks/useCaseInputStorage';
import { useClearCaseError, useUpdateCaseInput } from '@/hooks/useCases';
import { useCanCallAPI } from '@/hooks/usePermissions';
import { useClosePortalSession } from '@/hooks/usePortalSession';
import { useCurrentUser } from '@/hooks/useUsers';
import type { FieldGroup } from '@/types/formSchema';
import {
  computeAutoCalculatedValue,
  findFieldById,
  getAutoCalculatedFieldIds,
  getMirrorUpdates,
} from '@/utils/autoCalculate';
import { initializeFormData, validateGroup } from '@/utils/cases';
import { applyRequiredFieldsToGroups, getRequiredFieldsFromGroups } from '@/utils/schemaValidation';

import { AudioPlayer, type AudioPlayerHandle } from './AudioPlayer/AudioPlayer';
import { DocumentPreviewTab } from './DocumentPreviewTab';
import { AudioErrorBoundary } from './ErrorBoundary/AudioErrorBoundary';
import { TranscriptErrorBoundary } from './ErrorBoundary/TranscriptErrorBoundary';
import { PortalTab } from './portal/PortalTab';
import { Transcript } from './Transcript/Transcript';

/** Preview mode for the right column */
enum PreviewMode {
  DOCUMENT = 'DOCUMENT',
  TRANSCRIPT = 'TRANSCRIPT',
  PORTAL = 'PORTAL',
}

/**
 * Whether a case is expected to get a call recording. Manually created cases
 * and cases created by an external integration through the API ('api') have
 * no recording, so the "Recording will be available shortly" copy is wrong
 * for them.
 */
const isRecordingBacked = (creationMethod: string | undefined): boolean =>
  creationMethod !== 'manual' && creationMethod !== 'api';

interface KnipperCaseSummaryViewProps {
  caseId: string;
  caseDetails: any;
  fieldGroups: FieldGroup[];
  /** Output field groups from workflow.ui.output */
  outputGroups?: FieldGroup[];
  /** Current case state */
  caseState?: KnipperCaseState;
  /** Document asset ID for download */
  documentAssetId?: string | null;
  /** Callback to download the document */
  onDownloadDocument?: (assetId: string) => void;
  /** Callback to attach a recording to the case */
  onSelectCall?: (callId: string, onSuccess?: () => void) => void;
  /** Whether there are multiple recordings */
  hasMultipleRecordings?: boolean;
  /** Number of recordings available */
  recordingCount?: number;
  /** Callback when "View Recordings" is clicked */
  onViewRecordings?: () => void;
  /** Whether this is a portal-variant workflow (hides document preview, shows portal tab) */
  isPortalVariant?: boolean;
}

/** Handle type for imperative methods exposed by KnipperCaseSummaryView */
export interface KnipperCaseSummaryViewHandle {
  /** Validates all fields across all groups. Returns true if valid, false if errors found. */
  validateAll: () => boolean;
  /** Saves unsaved form data. Returns true if save succeeded (or no changes), false on error. */
  saveFormData: () => Promise<boolean>;
}

export const KnipperCaseSummaryView = forwardRef<
  KnipperCaseSummaryViewHandle,
  KnipperCaseSummaryViewProps
>(
  (
    {
      caseDetails,
      fieldGroups,
      outputGroups,
      caseState,
      documentAssetId,
      onDownloadDocument,
      onSelectCall,
      hasMultipleRecordings: hasMultipleRecordingsProp,
      recordingCount: recordingCountProp,
      onViewRecordings,
      isPortalVariant,
    },
    ref,
  ) => {
    const { caseId } = useParams<{
      caseId?: string;
    }>();

    const isTerminalState =
      caseState === KnipperCaseState.SUBMITTED ||
      caseState === KnipperCaseState.ACKNOWLEDGED ||
      caseState === KnipperCaseState.COMPLETED ||
      caseState === KnipperCaseState.NON_REPORTABLE ||
      caseState === KnipperCaseState.DUPLICATE ||
      caseState === KnipperCaseState.CANCELLED ||
      caseState === KnipperCaseState.DELIVERING_EMAIL;

    // Close portal session on unmount (in-app navigation away from this case).
    // Page reload / tab close is handled by PQCAgent detecting Browserbase session expiry.
    // Skip for terminal states — the workflow is already completed.
    const shouldCleanupSession = isPortalVariant && !isTerminalState;
    const closePortalSession = useClosePortalSession(shouldCleanupSession ? caseId : undefined);

    useEffect(() => {
      if (!shouldCleanupSession) return;
      return () => {
        closePortalSession();
      };
    }, [shouldCleanupSession, closePortalSession]);

    const [selectPreview, setSelectPreview] = useState<PreviewMode>(
      isPortalVariant ? PreviewMode.PORTAL : PreviewMode.DOCUMENT,
    );
    // When portal is active (iframe showing), collapse the left column to give it full width
    const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
    const [edit, setEdit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [caseError, setCaseError] = useState<ErrorWithDetails | null>(null);

    // Multiple recordings modal state (banner state is controlled by parent for REVIEW_NEEDED)
    const [isRecordingsModalOpen, setIsRecordingsModalOpen] = useState(false);

    // Audio player ref and state
    const audioPlayerRef = useRef<AudioPlayerHandle | null>(null);
    const hasShownErrorAnimation = useRef(false);

    const { getSelectedWorkflow, user, selectedProgramId } = useStore();
    const selectedWorkflow = getSelectedWorkflow();

    // Check if user has resubmission permission (gates edit in submitted/completed states)
    const hasResubmissionPermission = useCanCallAPI(
      API_METHODS.ManualActionService.ExecuteResendEmailAction,
    );

    // Fetch schema from schema service (pass workflow_type so PQC uses local JSON schema)
    const { inputGroups } = useSchema(selectedWorkflow?.id);

    // Fetch full user data from API to get title and other fields
    const { data: currentUserResponse } = useCurrentUser();
    const fullUserData = currentUserResponse?.user || user;

    const existingInputData = useMemo(() => {
      return caseDetails?.input_data?.data || caseDetails?.input_data || null; // data is similar to caseinput.data
    }, [caseDetails]);

    const outputData = useMemo(() => {
      return caseDetails?.output_data?.data || caseDetails?.output_data || {};
    }, [caseDetails]);

    // Show output section in SUBMITTED/ACKNOWLEDGED/COMPLETED states, or in REVIEW_NEEDED if ack_id
    // is present in output. Warm transfer now models ack_id as an output field captured at create
    // (AQ-357), so its output resolves to just that field and renders here like other programs —
    // no variant gate needed. (The previous !isWarmTransfer workaround existed only because an empty
    // WarmTransferOutput made the resolver echo the whole schema; with ack_id in output that no
    // longer happens.)
    const hasAckId = !!outputData?.ack_id;
    const showOutputSection =
      outputGroups &&
      outputGroups.length > 0 &&
      (caseState === KnipperCaseState.SUBMITTED ||
        caseState === KnipperCaseState.ACKNOWLEDGED ||
        caseState === KnipperCaseState.COMPLETED ||
        (caseState === KnipperCaseState.REVIEW_NEEDED && hasAckId));

    // Sync case error from caseDetails when it changes
    useEffect(() => {
      if (caseDetails?.error) {
        // setting the default error message to empty string to avoid showing generic error message
        setCaseError({ ...caseDetails.error, message: '' });
      } else {
        setCaseError(null);
      }
    }, [caseDetails?.error]);

    // Format error message for tracking changes
    const formattedErrorMessage = useMemo(() => {
      if (!caseError) return null;
      return formatErrorMessage(caseError, fieldGroups);
    }, [caseError, fieldGroups]);

    // Build field errors map to mark fields as having errors (for destructive styling)
    const caseFieldErrors = useMemo(() => {
      const errors: Record<string, string> = {};
      if (!caseError?.details) return errors;

      const { missing_fields = [], invalid_fields = [], field_errors = [] } = caseError.details;

      // Mark missing fields
      missing_fields.forEach((fieldId: string) => {
        errors[fieldId] = 'Value cannot be empty';
      });

      // Mark invalid fields
      invalid_fields.forEach((fieldId: string) => {
        errors[fieldId] = 'Invalid value';
      });

      // Mark fields with nested per-field errors (field_errors from the PQC agent)
      field_errors.forEach(fieldError => {
        if (fieldError.field && !errors[fieldError.field]) {
          errors[fieldError.field] = humanizeFieldErrorInline(fieldError);
        }
      });

      return errors;
    }, [caseError]);

    // Merge case errors with field validation errors
    const allFieldErrors = useMemo(() => {
      return { ...caseFieldErrors, ...fieldErrors };
    }, [caseFieldErrors, fieldErrors]);

    const [expandedItems, setExpandedItems] = useState<string[]>(() =>
      createAllAccordionIndices(fieldGroups.length),
    );

    // Fetch HTML template from the Template API (only for email variant — portal doesn't use it)
    // First try workflow-specific, then fall back to program-wide if not found.
    const { data: workflowTemplatesData, isLoading: isLoadingWorkflowTemplate } = useTemplates({
      workflowId: selectedWorkflow?.id,
      slug: 'pqc-manufacturer-form',
      enabled: !isPortalVariant && !!selectedWorkflow?.id,
    });
    const workflowTemplateId = workflowTemplatesData?.templates?.[0]?.id;

    const { data: programTemplatesData, isLoading: isLoadingProgramTemplate } = useTemplates({
      programId: selectedProgramId ?? undefined,
      slug: 'pqc-manufacturer-form',
      enabled: !isPortalVariant && !workflowTemplateId && !isLoadingWorkflowTemplate,
    });

    const htmlTemplateId = workflowTemplateId ?? programTemplatesData?.templates?.[0]?.id;
    const isLoadingTemplate =
      isLoadingWorkflowTemplate || (!workflowTemplateId && isLoadingProgramTemplate);

    // Render the template server-side via the Template API
    const renderTemplateMutation = useRenderTemplate();

    // Initialize form data from existing data
    const initializedFormData = useMemo(
      () => initializeFormData(inputGroups, existingInputData),
      [inputGroups, existingInputData],
    );

    const [audioCurrentTime, setAudioCurrentTime] = useState(0);

    const handleSegmentClick = useCallback((time: number) => {
      audioPlayerRef.current?.seekTo(time);
    }, []);

    // Capture original form data when existingInputData changes (data from server)
    // This is used to reset the form when user cancels edits
    const originalFormData = useMemo(() => {
      return initializeFormData(inputGroups, existingInputData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingInputData]);

    const updateCaseInput = useUpdateCaseInput();
    const clearCaseError = useClearCaseError();

    const { selectedCallId, setSelectedCallId } = useStore();

    const { data: callsData } = useCaseRecordCalls(caseId || '', {
      // use this only when transcript is clicked
      refetchInterval: 30000,
    });

    // Get the selected call's embedded assets from the calls list response
    // The listAssets API doesn't filter by call_id, so we use embedded IDs instead
    const selectedCallEmbeddedAssets = useMemo(() => {
      if (!selectedCallId || !callsData?.calls) return undefined;
      const call = callsData.calls.find((c: CallInfo) => c.id === selectedCallId);
      return call?.assets?.[0];
    }, [selectedCallId, callsData?.calls]);

    const transcriptAssets = useMemo((): Asset[] => {
      const transcriptId = selectedCallEmbeddedAssets?.transcript;
      if (!transcriptId) return [];
      return [{ id: transcriptId } as Asset];
    }, [selectedCallEmbeddedAssets?.transcript]);
    const transcriptAssetsLoading = selectedCallId != null && !callsData?.calls;

    const { data: selectedCallData } = useCall(caseId || null, selectedCallId || null, {
      refetchInterval: 10000,
    });
    const callDetails = selectedCallData?.call;

    // Extract AI suggestions and hints from call details (memoized for stable references)
    const extractedInputData = useMemo(
      () => callDetails?.extracted_data?.input?.data || null,
      [callDetails?.extracted_data?.input?.data],
    );
    const extractedInputHints = useMemo(
      () => callDetails?.extracted_data?.input?.metadata || null,
      [callDetails?.extracted_data?.input?.metadata],
    );

    // Use localStorage hook for form data persistence (after selectedCallId is available)
    const { formData, setFormDataBulk, getFieldSuggestionProps, clearStorage } =
      useCaseInputStorage({
        caseId,
        callId: selectedCallId,
        initialInputData: initializedFormData,
        extractedInputData,
        extractedInputHints,
      });

    // Apply schema-based required fields to fieldGroups based on current formData
    // This ensures validation and display use the same required field logic
    const fieldGroupsWithRequired = useMemo(() => {
      return applyRequiredFieldsToGroups(fieldGroups, formData);
    }, [fieldGroups, formData]);

    // Filter out auto-calculated (inferred) and hidden fields from visible groups
    const autoCalculatedIds = getAutoCalculatedFieldIds(fieldGroupsWithRequired);
    const visibleFieldGroups = fieldGroupsWithRequired.map(group => ({
      ...group,
      fields: group.fields.filter(
        f =>
          !autoCalculatedIds.has(f.id) &&
          !f.hidden &&
          !(
            f.hideWhenField &&
            f.hideWhenValue &&
            String(formData[f.hideWhenField]) === f.hideWhenValue
          ),
      ),
    }));

    const audioAssets = useMemo((): Asset[] => {
      const recordingId = selectedCallEmbeddedAssets?.recording;
      if (!recordingId) return [];
      return [{ id: recordingId } as Asset];
    }, [selectedCallEmbeddedAssets?.recording]);

    // Get the status of the selected call
    const selectedCallStatus = useMemo(() => {
      if (!selectedCallId || !callsData?.calls) return undefined;
      const selectedCall = callsData.calls.find(call => call.id === selectedCallId);
      return selectedCall?.status;
    }, [selectedCallId, callsData?.calls]);

    // Check if there are multiple recordings for the banner (use prop if provided, else compute)
    const hasMultipleRecordingsLocal = (callsData?.calls?.length || 0) > 1;
    const hasMultipleRecordings = hasMultipleRecordingsProp ?? hasMultipleRecordingsLocal;
    const recordingCount = recordingCountProp ?? (callsData?.calls?.length || 0);

    // Check if user can attach recordings (only in review_needed state for this component)
    const canAttachRecording = caseState === KnipperCaseState.REVIEW_NEEDED;

    // Get the currently attached call ID from case's contact_id
    const currentlyAttachedCallId = useMemo(() => {
      const caseContactId = caseDetails?.input_data?.data?.contact_id as string | undefined;
      if (!caseContactId || !callsData?.calls) return undefined;
      const matchingCall = callsData.calls.find(
        call => call.input_data?.contact_id === caseContactId,
      );
      return matchingCall?.id;
    }, [caseDetails?.input_data?.data?.contact_id, callsData?.calls]);

    // Handle attach recording action - pass modal close as onSuccess callback
    const handleAttachRecording = useCallback(
      (callId: string) => {
        onSelectCall?.(callId, () => setIsRecordingsModalOpen(false));
      },
      [onSelectCall],
    );

    const saveFormData = useCallback(
      // api call after saving data
      // Returns true if save succeeded (or nothing to save), false on error
      async (showSuccessToast = false): Promise<boolean> => {
        if (!caseId || !caseDetails) {
          return true;
        }

        try {
          const updatedInputData = {
            schema_version: '1.0',
            data: formData,
          };

          await new Promise((resolve, reject) => {
            updateCaseInput.mutate(
              { caseId, inputData: updatedInputData },
              {
                onSuccess: resolve,
                onError: reject,
              },
            );
          });

          // In review with a PQC agent validation error set: the user has
          // addressed the flagged fields, so clear the persisted error after a
          // successful save. Non-blocking — the workflow re-validates at
          // submission anyway, so a failed clear only leaves a stale banner.
          if (caseState === KnipperCaseState.REVIEW_NEEDED && caseDetails.error) {
            clearCaseError.mutate({ caseId });
          }

          // Clear localStorage after successful save
          clearStorage();

          if (showSuccessToast) {
            toast.success('Case input updated successfully');
          }
          return true;
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to update case input';
          toast.error(errorMessage);
          return false;
        }
      },
      [caseId, caseDetails, formData, updateCaseInput, clearCaseError, caseState, clearStorage],
    );

    // Render template server-side when template ID and case data are available
    // (skipped for portal variant — no document preview)
    const inputDataString = JSON.stringify(caseDetails?.input_data);
    useEffect(() => {
      if (isPortalVariant) return;
      if (htmlTemplateId && caseId) {
        const templateContext: Record<string, string> = {};
        if (fullUserData?.name) {
          templateContext.user_full_name = fullUserData.name;
        }
        if (fullUserData?.title) {
          templateContext.user_title = fullUserData.title;
        }
        if (fullUserData?.email) {
          templateContext.user_email = fullUserData.email;
        }
        const now = new Date();
        templateContext.current_date = now.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        });
        templateContext.current_time = now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        templateContext.current_am_pm = now.getHours() < 12 ? 'AM' : 'PM';

        // Date of Call — the originating (earliest) linked call's start date,
        // formatted DD-MMM-YYYY to match the generated .xlsx. Omitted when the
        // case has no call (e.g. manually-created), so the field renders blank.
        const callStartTimes = (callsData?.calls ?? [])
          .map(c => c.started_at)
          .filter((s): s is string => !!s)
          .map(s => new Date(s).getTime())
          .filter(t => !Number.isNaN(t));
        if (callStartTimes.length > 0) {
          templateContext.call_date = new Date(Math.min(...callStartTimes))
            .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            .replace(/ /g, '-');
        }

        renderTemplateMutation.mutate({
          templateId: htmlTemplateId,
          caseRecordId: caseId,
          context: templateContext,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      htmlTemplateId,
      caseId,
      inputDataString,
      fullUserData?.name,
      fullUserData?.title,
      fullUserData?.email,
      callsData?.calls,
    ]);

    // Initialize accordion expanded items when groups change (default to expanded)
    React.useEffect(() => {
      setExpandedItems(fieldGroups.map((_, index) => index.toString()));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldGroups.length]);

    // Auto-select first call when calls load
    useEffect(() => {
      if (callsData?.calls && callsData.calls.length > 0 && !selectedCallId) {
        const firstCallId = callsData.calls[0].id;
        if (firstCallId) {
          setSelectedCallId(firstCallId);
        }
      }
    }, [callsData?.calls, selectedCallId, setSelectedCallId]);

    // Handle accordion toggle
    const handleToggleAll = () => {
      setExpandedItems(toggleAllAccordionItems(expandedItems, fieldGroups.length));
    };

    // Handle group edit click - switch to edit mode and show transcript tab (only if there's a call)
    const handleGroupEditClick = () => {
      setEdit(true);
      // Only switch to transcript tab if there's a call recording to reference
      if (selectedCallId) {
        setSelectPreview(PreviewMode.TRANSCRIPT);
      }
    };

    const handleEditCancel = () => {
      setEdit(false);
      setSelectPreview(isPortalVariant ? PreviewMode.PORTAL : PreviewMode.DOCUMENT);
      setFormDataBulk(originalFormData);
      setFieldErrors({});
    };

    const handleEditSave = async () => {
      if (isSaving) return; // Prevent double-clicks
      if (!validateForm()) return;

      setIsSaving(true);
      try {
        // Reset animation flag before saving so errors after save will animate
        hasShownErrorAnimation.current = false;
        await saveFormData();
        setEdit(false);
        setSelectPreview(isPortalVariant ? PreviewMode.PORTAL : PreviewMode.DOCUMENT);
      } finally {
        setIsSaving(false);
      }
    };

    const handleDataChange = (value: any, fieldPath?: string) => {
      // Auto-calculate derived fields when a source field changes
      let updatedValue = value;
      if (fieldPath) {
        const sourceField = findFieldById(fieldGroupsWithRequired, fieldPath);
        if (sourceField?.autoCalculates) {
          const result = computeAutoCalculatedValue(sourceField, value[fieldPath]);
          if (result) {
            updatedValue = { ...updatedValue, [result.targetFieldId]: result.value };
          }
        }

        // Mirror fields based on schema-driven mirrorWhenField/mirrorWhenValue conditions
        const mirrorUpdates = getMirrorUpdates(
          fieldGroupsWithRequired,
          value,
          fieldPath,
          value[fieldPath],
        );
        if (Object.keys(mirrorUpdates).length > 0) {
          updatedValue = { ...updatedValue, ...mirrorUpdates };
        }
      }

      setFormDataBulk(updatedValue);

      // Clear from validation errors
      setFieldErrors(prev => {
        const newErrors = { ...prev };

        // Clear error for the changed field
        if (fieldPath && newErrors[fieldPath]) {
          delete newErrors[fieldPath];
        }

        // When case_type changes, clear errors for fields that are no longer required
        if (fieldPath === 'case_type') {
          const newRequiredFields = getRequiredFieldsFromGroups(fieldGroups, value);
          // Remove errors for fields that are no longer required
          Object.keys(newErrors).forEach(errorFieldId => {
            if (!newRequiredFields.has(errorFieldId)) {
              delete newErrors[errorFieldId];
            }
          });
        }

        return Object.keys(newErrors).length !== Object.keys(prev).length ? newErrors : prev;
      });

      // Clear from case errors when field is edited
      if (fieldPath && caseError?.details) {
        const { missing_fields = [], invalid_fields = [], field_errors = [] } = caseError.details;

        if (
          missing_fields.includes(fieldPath) ||
          invalid_fields.includes(fieldPath) ||
          field_errors.some(fieldError => fieldError.field === fieldPath)
        ) {
          setCaseError({
            ...caseError,
            details: {
              ...caseError.details,
              missing_fields: missing_fields.filter(id => id !== fieldPath),
              invalid_fields: invalid_fields.filter(id => id !== fieldPath),
              field_errors: field_errors.filter(fieldError => fieldError.field !== fieldPath),
            },
          });
        }
      }
    };

    const scrollToErrorField = useCallback(
      (errorFieldId: string) => {
        // Small delay to ensure DOM is updated with error state
        setTimeout(() => {
          const fieldElement = document.querySelector(`[data-field-id="${errorFieldId}"]`);
          if (fieldElement) {
            // Find which accordion group this field belongs to
            const groupIndex = fieldGroups.findIndex(group =>
              group.fields.some(field => field.id === errorFieldId),
            );

            // Expand the accordion if not already expanded
            if (groupIndex !== -1 && !expandedItems.includes(groupIndex.toString())) {
              setExpandedItems(prev => [...prev, groupIndex.toString()]);

              // Wait for accordion to expand before scrolling
              setTimeout(() => {
                fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            } else {
              fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }, 100);
      },
      [fieldGroups, expandedItems],
    );

    const validateForm = useCallback((): boolean => {
      if (!fieldGroupsWithRequired) return false;

      // Validate all groups using schema-based required fields
      const errors: Record<string, string> = {};

      fieldGroupsWithRequired.forEach(fieldGroup => {
        const groupErrors = validateGroup(fieldGroup, formData);
        Object.assign(errors, groupErrors);
      });

      if (Object.keys(errors).length > 0) {
        setFieldErrors(prev => ({ ...prev, ...errors }));

        // Scroll to the first error field
        const firstErrorFieldId = Object.keys(errors)[0];
        if (firstErrorFieldId) {
          scrollToErrorField(firstErrorFieldId);
        }

        return false;
      }
      return true;
    }, [fieldGroupsWithRequired, formData, scrollToErrorField]);

    // Expose validateAll and saveFormData methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validateAll: () => {
          const isValid = validateForm();
          if (!isValid) {
            // Switch to edit mode so user can fix errors
            setEdit(true);
          }
          return isValid;
        },
        saveFormData: () => saveFormData(),
      }),
      [validateForm, saveFormData],
    );

    return (
      <div className="flex flex-1 min-h-0 h-full">
        {/* Left Column - Field Data */}
        {isLeftCollapsed ? (
          <div className="shrink-0 border-r flex flex-col items-center py-4 w-10 transition-all duration-200">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 p-0"
              onClick={() => setIsLeftCollapsed(false)}
              title="Expand Case Review"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground mt-2 [writing-mode:vertical-lr] rotate-180">
              Case Review
            </span>
          </div>
        ) : (
          <div className="w-[50%] border-r flex flex-col p-4 gap-4 min-h-0">
            <div className="shrink-0">
              <div className="flex items-center justify-between">
                <h4 className="text-muted-foreground font-medium text-sm flex items-center gap-2">
                  Case Review
                  {caseDetails?.error && <CircleAlert className="h-4 w-4 text-destructive" />}
                </h4>
                <div className="flex items-end gap-2">
                  {/* Expand/Collapse All button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleAll}
                    className="h-5 px-2 text-xs"
                  >
                    <ChevronsDownUp className="w-3 h-3 mr-1" />
                    {expandedItems.length === fieldGroups.length ? 'Collapse All' : 'Expand All'}
                  </Button>
                  <div className="flex">
                    {!edit &&
                      caseDetails?.status_flags?.can_update_input &&
                      // In submitted/completed states, only users with resubmission permission can edit
                      (caseState === KnipperCaseState.SUBMITTED ||
                      caseState === KnipperCaseState.COMPLETED ? (
                        <PermissionGuard
                          apiMethod={API_METHODS.ManualActionService.ExecuteResendEmailAction}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={e => {
                              e.stopPropagation();
                              handleGroupEditClick();
                            }}
                            className="h-5 px-2 text-xs"
                          >
                            <Edit3 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        </PermissionGuard>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            handleGroupEditClick();
                          }}
                          className="h-5 px-2 text-xs"
                        >
                          <Edit3 className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      ))}
                    {edit && (
                      <div className="flex">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            handleEditCancel();
                          }}
                          className="h-5 px-2 text-xs mr-3"
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            handleEditSave();
                          }}
                          className="h-5 px-2 text-xs"
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {formattedErrorMessage && (
                <motion.div
                  initial={!hasShownErrorAnimation.current ? { x: 0 } : false}
                  animate={
                    !hasShownErrorAnimation.current
                      ? {
                          x: [0, -2, 2, -2, 2, -1, 1, 0],
                        }
                      : {}
                  }
                  transition={
                    !hasShownErrorAnimation.current
                      ? {
                          duration: 1,
                          delay: 0.5,
                          ease: 'circInOut',
                        }
                      : {}
                  }
                  onAnimationComplete={() => {
                    hasShownErrorAnimation.current = true;
                  }}
                >
                  <Alert variant="destructive" density="compact" className="mt-2">
                    <AlertDescription className="whitespace-pre-line text-destructive max-h-32 overflow-y-auto w-full">
                      {formattedErrorMessage}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {/* Reason Section - shown for terminal states (duplicate, cancelled, non_reportable) */}
              {(caseState === KnipperCaseState.NON_REPORTABLE ||
                caseState === KnipperCaseState.DUPLICATE ||
                caseState === KnipperCaseState.CANCELLED) &&
                caseId && (
                  <CaseStateReasonSection
                    caseId={caseId}
                    caseState={caseState}
                    actionByUserId={caseDetails?.cancelled_by || caseDetails?.updated_by}
                    actionAt={caseDetails?.state_updated_at || caseDetails?.cancelled_at}
                  />
                )}
              {/* Manufacturer Acknowledgement Section - only in SUBMITTED state */}
              {showOutputSection && (
                <ManufacturerAckSection
                  caseId={caseId!}
                  outputGroups={outputGroups!}
                  outputData={outputData}
                  canEdit={caseDetails?.status_flags?.can_update_output ?? false}
                  isPortalVariant={isPortalVariant}
                  workflowCompletedAt={caseDetails?.workflow_completed_at}
                />
              )}
              <FieldGroupAccordion
                groups={visibleFieldGroups}
                data={formData}
                editableState={null}
                isEditable={edit}
                caseRecordId={caseId}
                showCollapseAll={false} // Disable internal button since we have it at header level
                externalExpandedItems={expandedItems}
                fieldError={allFieldErrors}
                isCaseReview={true}
                onExpandedItemsChange={setExpandedItems}
                onGroupEditClick={
                  !edit &&
                  !(
                    (caseState === KnipperCaseState.SUBMITTED ||
                      caseState === KnipperCaseState.COMPLETED) &&
                    !hasResubmissionPermission
                  )
                    ? handleGroupEditClick
                    : undefined
                }
                onDataChange={handleDataChange}
                defaultExpanded={true}
                getFieldSuggestionProps={getFieldSuggestionProps}
                emptyStateMessage={{
                  title: 'No Request Data Available',
                  subtitle: 'Request data will appear here',
                }}
                className={{
                  accordion: 'space-y-4',
                  accordionContent: 'px-0 pb-0',
                  fieldContainer: 'space-y-0',
                  fieldSeparator: '',
                }}
                fieldClassName={{
                  container: 'group px-3 py-2 hover:bg-muted transition-colors',
                  label: 'text-sm text-muted-foreground min-w-[200px]',
                  value: 'text-sm text-right flex-1 ml-4',
                  editButton:
                    'p-1 hover:bg-muted rounded text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-2',
                  nonEditingLayout: 'flex justify-between items-start',
                  labelInline: true,
                }}
              />
            </div>
          </div>
        )}

        {/* Right Column - Preview / Transcript / Portal */}
        <div
          className={`${isLeftCollapsed ? 'flex-1' : 'min-w-[600px] w-2/3'} flex flex-col min-h-0`}
        >
          {/* Multiple Recordings Banner */}
          {hasMultipleRecordings && onViewRecordings && (
            <MultipleRecordingsBanner
              recordingCount={recordingCount || 0}
              onViewRecordings={onViewRecordings}
            />
          )}
          <div className="shrink-0 flex items-center gap-2 px-4 pt-4">
            {!isPortalVariant && (
              <div
                className={`
              py-1
              text-sm font-medium
              cursor-pointer
              transition-colors
              flex
              border-b-2
              ${
                selectPreview === PreviewMode.DOCUMENT
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:border-primary hover:text-foreground'
              }
           `}
              >
                <h4
                  onClick={() => {
                    setSelectPreview(PreviewMode.DOCUMENT);
                  }}
                >
                  Document Preview
                </h4>
                {documentAssetId && onDownloadDocument && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onDownloadDocument(documentAssetId)}
                    className="h-5 w-5 ml-2"
                    title="Download Preview Document"
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                )}
              </div>
            )}
            {isPortalVariant && (
              <h4
                onClick={() => setSelectPreview(PreviewMode.PORTAL)}
                className={`
                ml-3
                py-1
                text-sm font-medium
                cursor-pointer
                transition-colors
                flex items-center gap-1.5
                border-b-2
                ${
                  selectPreview === PreviewMode.PORTAL
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:border-primary hover:text-foreground'
                }
              `}
              >
                <Globe className="h-3.5 w-3.5" />
                {isTerminalState ? 'Portal Confirmation' : 'Portal'}
              </h4>
            )}
            <SimpleTooltip
              content={
                !selectedCallId
                  ? isRecordingBacked(caseDetails?.creation_method)
                    ? 'Recording will be available shortly'
                    : caseDetails?.creation_method === 'api'
                      ? 'Case created via API. No call recording available for this case'
                      : 'Case created manually. No call recording available for this case'
                  : null
              }
              side="bottom"
            >
              <h4
                onClick={() => {
                  if (selectedCallId) {
                    setSelectPreview(PreviewMode.TRANSCRIPT);
                  }
                }}
                className={`
                ml-3
                py-1
                text-sm font-medium
                transition-colors
                flex
                border-b-2
                ${
                  !selectedCallId
                    ? 'cursor-not-allowed text-muted-foreground/50 border-transparent'
                    : selectPreview === PreviewMode.TRANSCRIPT
                      ? 'cursor-pointer border-primary text-foreground'
                      : 'cursor-pointer border-transparent text-muted-foreground hover:border-primary hover:text-foreground'
                }
           `}
              >
                Transcript
              </h4>
            </SimpleTooltip>
            {isLeftCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 ml-auto"
                onClick={() => setIsLeftCollapsed(false)}
                title="Show Case Review"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </div>
          {selectPreview === PreviewMode.TRANSCRIPT && selectedCallId && (
            <div className="flex flex-col h-full min-h-0">
              <div className="flex-1 min-h-0">
                <TranscriptErrorBoundary>
                  <Transcript
                    className="h-full"
                    caseRecordId={caseId!}
                    callId={selectedCallId}
                    assets={transcriptAssets}
                    isLoadingAssets={transcriptAssetsLoading}
                    callSid={callDetails?.sid}
                    currentTime={audioCurrentTime}
                    onSegmentClick={handleSegmentClick}
                    callStatus={selectedCallStatus}
                  />
                </TranscriptErrorBoundary>
              </div>
              <div className="sticky bottom-0 border-t bg-background z-10">
                <AudioErrorBoundary>
                  <AudioPlayer
                    ref={audioPlayerRef}
                    caseRecordId={caseId!}
                    caseCode={caseDetails?.code}
                    callId={selectedCallId}
                    callDetails={callDetails}
                    onTimeUpdate={setAudioCurrentTime}
                    assets={audioAssets}
                  />
                </AudioErrorBoundary>
              </div>
            </div>
          )}
          {selectPreview === PreviewMode.DOCUMENT && !isPortalVariant && (
            <div className="flex-1 overflow-y-auto min-h-0 border-t">
              <DocumentPreviewTab
                isLoading={isLoadingTemplate || renderTemplateMutation.isPending}
                isError={renderTemplateMutation.isError}
                errorMessage={renderTemplateMutation.error?.message}
                htmlContent={renderTemplateMutation.data?.rendered?.html?.content}
                hasTemplate={!!htmlTemplateId}
              />
            </div>
          )}
          {selectPreview === PreviewMode.PORTAL && isPortalVariant && (
            <div className="flex-1 flex flex-col min-h-0 border-t">
              <PortalTab
                caseId={caseId || ''}
                caseDetails={caseDetails}
                isTerminalState={isTerminalState}
                onStartSession={() => setIsLeftCollapsed(true)}
              />
            </div>
          )}
        </div>

        {/* Recording Selection Modal */}
        <RecordingSelectionModal
          isOpen={isRecordingsModalOpen}
          onClose={() => setIsRecordingsModalOpen(false)}
          calls={callsData?.calls || []}
          caseId={caseId || ''}
          canAttach={canAttachRecording}
          onAttach={handleAttachRecording}
          currentlyAttachedCallId={currentlyAttachedCallId}
        />
      </div>
    );
  },
);

KnipperCaseSummaryView.displayName = 'KnipperCaseSummaryView';
